import React, { useEffect, useState } from 'react';
import { Table, Card, Input, Select, Space, message, Typography } from 'antd';
import type { TableProps } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ApiService from '../service/api-service';
import { Echo, GetEchosParams, SortField, SortOrder, EchoProduct } from '../types/echo';
import { Product } from '../types/product';

const { Option } = Select;
const { Title } = Typography;

const PreviousEchos: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Echo[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<SortField>(SortField.CREATED_AT);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  const fetchEchos = async (params: GetEchosParams = {}) => {
    try {
      setLoading(true);
      const response = await ApiService.getEchos(params);
      
      if (response.success) {
        setData(response.data.echos);
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.limit,
          total: response.data.pagination.total
        });
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to fetch echos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEchos();
  }, []);

  const handleTableChange: TableProps<Echo>['onChange'] = (paginationParams, _, sorter) => {
    const params: GetEchosParams = {
      page: paginationParams.current,
      limit: paginationParams.pageSize,
      search: searchText,
      sortBy: sortField,
      sortOrder: sortOrder
    };

    fetchEchos(params);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchEchos({
      page: 1,
      limit: pagination.pageSize,
      search: value,
      sortBy: sortField,
      sortOrder: sortOrder
    });
  };

  const handleSortChange = (value: SortField) => {
    setSortField(value);
    fetchEchos({
      page: pagination.current,
      limit: pagination.pageSize,
      search: searchText,
      sortBy: value,
      sortOrder: sortOrder
    });
  };

  const handleSortOrderChange = (value: SortOrder) => {
    setSortOrder(value);
    fetchEchos({
      page: pagination.current,
      limit: pagination.pageSize,
      search: searchText,
      sortBy: sortField,
      sortOrder: value
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <span className="font-medium text-gray-800">{text}</span>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => <span className="text-gray-600">{text}</span>
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
      render: (product: Product) => (
        <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm">
          {product.name}
        </span>
      )
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      render: (text: string) => (
        <span className="text-gray-700">
          {text.charAt(0) + text.slice(1).toLowerCase()}
        </span>
      )
    },
    {
      title: 'Speaker',
      dataIndex: 'speaker',
      key: 'speaker',
      render: (text: string) => (
        <span className="text-gray-700">
          {text.charAt(0) + text.slice(1).toLowerCase()}
        </span>
      )
    },
    {
      title: 'Valid Till',
      dataIndex: 'validTill',
      key: 'validTill',
      render: (date: string) => (
        <span className="text-gray-600">
          {dayjs(date).format('MMM D, YYYY')}
        </span>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <span className="text-gray-600">
          {dayjs(date).format('MMM D, YYYY')}
        </span>
      )
    }
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <Title level={4} className="text-gray-800 mb-2">Previous Echos</Title>
        <p className="text-gray-500">View and manage your previous interview practice sessions</p>
      </div>

      <Card className="shadow-sm rounded-lg border-gray-100">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <Input
            placeholder="Search echos..."
            prefix={<SearchOutlined className="text-gray-400" />}
            className="max-w-xs !rounded-lg"
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Space className="ml-auto">
            <span className="text-gray-500 flex items-center gap-2">
              <FilterOutlined /> Sort by:
            </span>
            <Select
              value={sortField}
              onChange={handleSortChange}
              className="w-40 !rounded-lg"
              popupClassName="rounded-lg"
            >
              <Option value={SortField.CREATED_AT}>Created At</Option>
              <Option value={SortField.NAME}>Name</Option>
              <Option value={SortField.PRODUCT}>Product</Option>
              <Option value={SortField.VALID_TILL}>Valid Till</Option>
            </Select>
            <Select
              value={sortOrder}
              onChange={handleSortOrderChange}
              className="w-32 !rounded-lg"
              popupClassName="rounded-lg"
            >
              <Option value={SortOrder.ASC}>Ascending</Option>
              <Option value={SortOrder.DESC}>Descending</Option>
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            ...pagination,
            className: "!rounded-lg",
            showTotal: (total) => `Total ${total} items`
          }}
          onChange={handleTableChange}
          loading={loading}
          onRow={(record) => ({
            onClick: () => navigate(`/echo/${record.id}/sessions`),
            className: 'cursor-pointer hover:bg-gray-50/80 transition-colors duration-150'
          })}
          className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-50 [&_.ant-table-thead_.ant-table-cell]:font-medium [&_.ant-table]:rounded-lg"
        />
      </Card>
    </div>
  );
};

export default PreviousEchos; 